import { Button, Card, ConfirmModal, Panel, Table } from '@/components/Bicester';
import Image from '@/components/Bicester/Image';
import { Form } from '@/components/formik-rsuite';
import { useRequest } from 'ahooks';
import { FormikProvider, useFormik } from 'formik';
import React, { useState, useEffect } from 'react';
import { IconButton, Alert } from 'rsuite';
import { defaultRecordInfo } from './UploadReceipt/constants';
import useTable from '@/hooks/useTable';
import {
  Cell,
  DateTimeCell,
  EditableCell,
  PriceCell,
  ShoppingVillageCell,
  UserNameCell,
} from '@/components/Bicester/Table/Cells';
import { BICESTER_FULL_DATE, BICESTER_FULL_DATE_HOURS_MINUTES } from '@/constants/dateFormat';
import useContentHeight from '@/hooks/useContentHeight';
import { useReadyInfo } from '@/providers/ReadyProvider';
import { ROLE_TYPE } from '@/constants';
import { fetchEditReceipt, fetchReceiptList } from '@/services/receipt/upload';
import _ from 'lodash';
import useReceiptBrandList from '@/hooks/preload/useReceiptBrandList';
import useSalesSource from '@/hooks/preload/useSalesSource';
import { fetchDeleteTicket } from '@/services/account';
import { useModal } from '@ebay/nice-modal-react';
import useGePcsList from '@/hooks/preload/useGePcsList';
import { getParams } from '@/services/utils';
import { getPrice } from '@/components/utils';
import { SERVICE_STATUS } from '@/services/constants';
import PageLoader from '@/components/shared/PageLoader';
import useLoading from '@/hooks/useLoading';
import { LAYOUT_CONFIG } from '@/constants/layoutConfig';
import { SearchParams } from '../Upload';

const ReceiptRecord = ({ searchParams }: { searchParams: SearchParams }) => {
  const modal = useModal(ConfirmModal);
  const { profile } = useReadyInfo();
  const { data: brandList, loading: brandListLoading } = useReceiptBrandList();
  const { data: salesSource, loading: salesSourceLoading } = useSalesSource();
  const { data: gePcsList, loading: gePcsListLoading } = useGePcsList();

  const [defaultInfo, setDefaultInfo] = useState(defaultRecordInfo);
  const [showTable, setShowTable] = useState(false);
  const [receiptList, setReceiptList] = useState([]);

  const tableHeight = `max(${
    useContentHeight({
      withToolBar: true,
      withPanelPaddingTop: true,
      withPanelPaddingBottom: true,
      withTablePagination: true,
    }) +
    (22 - 267 - 112)
  }px, ${
    LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT +
    LAYOUT_CONFIG.TABLE_PAGINATION_WITH_MARGIN_HEIGHT
  }px})`;
  const tableBodyHeight = Math.max(
    useContentHeight({
      withToolBar: true,
      withPanelPaddingTop: true,
      withPanelPaddingBottom: true,
      withTablePagination: true,
    }) +
      (22 - 267 - 112),
    LAYOUT_CONFIG.TABLE_MIN_ROWS_WITH_HEADER_HEIGHT
  );
  const {
    run: getReceiptList,
    data: receiptListData,
    loading: receiptListLoading,
  } = useRequest(fetchReceiptList, {
    manual: true,
    onSuccess: (res) => setReceiptList(transformData(res?.content_list ?? [])),
  });

  const { run: deleteTicket, loading: deleteTicketLoading } = useRequest(fetchDeleteTicket, {
    manual: true,
  });

  const { run: editReceipt, loading: editReceiptLoading } = useRequest(fetchEditReceipt, {
    manual: true,
  });

  const { params, onChange, onSearch, onRefresh } = useTable(getReceiptList);

  useEffect(() => {
    setDefaultInfo({
      ...defaultInfo,
      shopping_village: profile?.shopping_village,
    });
  }, []);

  useEffect(() => {
    if (!_.isNil(searchParams)) {
      const { values } = formik;
      formik.setValues({ ...values, ...searchParams });
      formik.submitForm();
    }
  }, [searchParams]);

  const transformData = (list: any[]) =>
    list.map((data) => {
      return {
        ...data,
        isEdit: false,
        hideBtn: false,
      };
    });

  const formik = useFormik({
    initialValues: defaultInfo,
    enableReinitialize: true, // initialValues默认值受控
    onSubmit(values, actions) {
      const params = Object.assign({}, _.omit(values, 'time_range'), {
        start_at:
          (values?.time_range && values?.time_range?.length === 2 && values?.time_range[0]) || '',
        end_at:
          (values?.time_range && values?.time_range?.length === 2 && values?.time_range[1]) || '',
      });
      onSearch(params);
      setShowTable(true);
    },
  });

  const loading = useLoading(brandListLoading || salesSourceLoading || gePcsListLoading);
  return (
    <>
      {loading && <PageLoader />}
      <Panel
        title="历史小票记录"
        subTitle={`总计 ${receiptListData?.total || 0} 条`}
        dividerHeight={15}
        leftShadow
        className="py-[25px]"
        style={{
          height: `${
            receiptListData?.content_list
              ? 'max(calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight) - 267px), auto)'
              : 'calc(100vh - var(--headerHeight) - var(--navBarHeight) - var(--toolBarHeight) - 267px)'
          }`,
        }}
        loading={deleteTicketLoading || editReceiptLoading}
      >
        <FormikProvider value={formik}>
          <Form>
            <Card horizontal>
              <Card.Picker label="购物村" formKey="shopping_village" pickerType="village" />
              <Card.Picker label="品牌" formKey="brand_id" pickerData={brandList} searchable />
              <Card.Input label="手机号" formKey="phone" />
              <Card.Input label="会员号" formKey="user_code" />
              <Card.DateRangePicker label="消费日期" formKey="time_range" placement="topEnd" />
              <IconButton
                type="submit"
                className="flex items-center w-[30px] ml-[10px]"
                style={{ height: 47 }}
              >
                <Image src="/resources/images/match.svg" />
              </IconButton>
            </Card>
          </Form>
        </FormikProvider>
        {showTable && (
          <Table
            style={{
              marginTop: 20,
              height: tableHeight,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
            loading={receiptListLoading}
            pageInfo={{ page: receiptListData?.current_page, total: receiptListData?.total }}
            data={receiptList}
            columns={[
              {
                columnProps: {
                  flexGrow: 1,
                },
                header: '客户名',
                cell: <UserNameCell dataKey="name" />,
              },
              {
                columnProps: {
                  flexGrow: 1.5,
                },
                header: '手机号',
                cell: <Cell dataKey="phone" />,
              },
              {
                columnProps: {
                  flexGrow: 1.5,
                },
                header: '消费日期',
                cell: <DateTimeCell dataKey="consume_time" format={BICESTER_FULL_DATE} />,
              },
              {
                columnProps: {
                  flexGrow: 1.5,
                },
                header: '消费品牌',
                cell: <Cell dataKey="brand" />,
              },
              {
                columnProps: {
                  flexGrow: 1.5,
                },
                header: '消费金额',
                cell: <PriceCell dataKey="price" />,
              },
              {
                columnProps: {
                  flexGrow: 1.5,
                },
                header: '上传购物村',
                cell: <ShoppingVillageCell dataKey="shopping_village" />,
              },
              {
                columnProps: {
                  flexGrow: 2,
                },
                header: '上传时间',
                cell: (
                  <DateTimeCell dataKey="create_at" format={BICESTER_FULL_DATE_HOURS_MINUTES} />
                ),
              },
              {
                columnProps: {
                  flexGrow: 2.5,
                },
                header: '上传方',
                cell: <Cell dataKey="type" />,
              },
              {
                columnProps: {
                  flexGrow: 2,
                },
                header: 'DRT销售',
                cell: (
                  <EditableCell
                    dataKey="pcs_id"
                    labelKey="pcs_name"
                    editComponent="picker"
                    pickerData={gePcsList.pcs_list}
                    searchable
                    menuMaxHeight={200}
                    setState={setReceiptList}
                    placement="topEnd"
                  />
                ),
              },
              {
                columnProps: {
                  flexGrow: 2,
                },
                header: '销售渠道',
                cell: (
                  <EditableCell
                    dataKey="sales_source"
                    labelKey="sales_source_name"
                    editComponent="picker"
                    pickerData={salesSource}
                    searchable
                    menuMaxHeight={200}
                    setState={setReceiptList}
                    placement="topEnd"
                  />
                ),
              },
              {
                columnProps: {
                  flexGrow: 1,
                },
                header: '操作',
                cell: (
                  <Cell>
                    {(rowData) =>
                      rowData?.hideBtn ? null : (
                        <div className="flex">
                          {rowData?.isEdit ? (
                            <IconButton
                              className="min-w-[15px] mr-[10px]"
                              onClick={() => {
                                modal.show({
                                  contentText: '保存该条记录修改吗？',
                                  submitText: '确认保存',
                                  cancelText: '取消',
                                  onSubmit: () => {
                                    editReceipt(
                                      getParams({
                                        id: rowData?.id,
                                        pcs_id: rowData?.pcs_id,
                                        sales_source: rowData?.sales_source,
                                      })
                                    ).then((res) => {
                                      if (res?.status === SERVICE_STATUS.SUCCESS) {
                                        Alert.success('保存成功');
                                        onRefresh();
                                      }
                                    });
                                  },
                                });
                              }}
                            >
                              <Image src="/resources/images/cell-save.svg" />
                            </IconButton>
                          ) : (
                            // 只有DRT可以修改小票列表的drt
                            profile?.role === ROLE_TYPE.DRT && (
                              <IconButton
                                className="min-w-[19px] mr-[10px]"
                                onClick={() =>
                                  setReceiptList((state) => {
                                    return state.map((item) => {
                                      if (item.id === rowData?.id) {
                                        return { ...item, isEdit: !item?.isEdit };
                                      } else {
                                        return { ...item, hideBtn: !item?.hideBtn };
                                      }
                                    });
                                  })
                                }
                              >
                                <Image src="/resources/images/cell-edit.svg" />
                              </IconButton>
                            )
                          )}
                          <Button
                            appearance="link"
                            onClick={() => {
                              const { id, brand, price } = rowData;
                              modal.show({
                                contentText: `确认删除 ${brand} ${getPrice(price)} 的小票记录吗？`,
                                submitText: '删除',
                                cancelText: '取消',
                                onSubmit: () => {
                                  deleteTicket(id).then((res) => {
                                    if (_.isString(res)) {
                                      Alert.success(res);
                                      onRefresh();
                                    }
                                  });
                                },
                              });
                            }}
                          >
                            删除
                          </Button>
                        </div>
                      )
                    }
                  </Cell>
                ),
              },
            ].filter(
              /**
               * GE迎宾大使、GE兼职：只有上传小票和搜索历史小票，不能修改删除
               */
              (column) =>
                !(
                  [ROLE_TYPE.AMBASSADOR, ROLE_TYPE.PT].includes(profile?.role) &&
                  column.header === '操作'
                )
            )}
            onChange={onChange}
          >
            <Table.Body height={tableBodyHeight} />
            <Table.Pagination />
          </Table>
        )}
      </Panel>
    </>
  );
};

export default ReceiptRecord;
